/**
 * Rambursuri curieri — ecranul refacut (12.10.2026), din macheta aprobata de Madalin.
 *
 * O lista de lucru in fata („De rezolvat"), coletele si incasarile in cate o fila, actiunile rare
 * intr-un meniu. Cine nu poate trimite in EA nu vede butonul de trimitere; ecranul intreg cere
 * `fan:read` (admin si contabilitate) — banii nu ajung la nimeni altcineva.
 */
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronDown, RefreshCw, Search } from 'lucide-react'
import { api } from '../../../lib/api'
import { useAuthStore } from '../../../stores/auth'
import { Button } from '../../../components/ui/Button'
import { DateField } from '../../../components/ui/DateField'
import { FiltruData } from '../../../components/ui/FiltruData'
import { Modal } from '../../../components/ui/Modal'
import { Pagination, type PageSize } from '../../../components/ui/Pagination'
import { CarduriSelector } from '../../../components/ui/CarduriSelector'
import { ChipuriFiltru } from '../../../components/ui/ChipuriFiltru'
import { ImportSameday, PotrivireManuala, SetariCurieri } from './ferestre'
import { PanouColet, TabelColete } from './colete'
import { BaraTrimitere, TabelIncasari, ViramentePeZile } from './incasari'
import { TrimitereAutomata } from './trimitereAutomata'
import { CompensareFan } from './compensareFan'
import {
  type DeRezolvat,
  type ExpeditiiResp,
  type FanStatus,
  type ListResponse,
  type Ramburs,
  type StareIncasare,
  STARI_INCASARE,
  descarcaFisier,
  lei,
} from './tipuri'

type Fila = 'rezolvat' | 'colete' | 'incasari'
type Lista = 'colete' | 'potrivit' | 'trimis'
type Curier = 'toti' | 'FAN' | 'SAMEDAY'

const cand = (s: string | null) =>
  s
    ? new Date(s).toLocaleString('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'niciodată'

export function RambursuriCurieriPage() {
  const permisiuni = useAuthStore((s) => s.permissions)
  const poateTrimite = permisiuni.includes('fan:post')

  /*
    Fila, cautarea si filtrul din adresa (`?tab=colete&q=<awb>`, `&stare=intarziate`): asa deschid
    coletul linkurile din alerta, din emailul catre admini si din cererea de marfa. `expeditii` =
    numele vechi al filei.
  */
  const [parametri] = useSearchParams()
  const qInitial = parametri.get('q') ?? ''
  const [fila, setFila] = useState<Fila>(() => {
    const t = parametri.get('tab')
    if (t === 'colete' || t === 'expeditii') return 'colete'
    if (t === 'incasari') return 'incasari'
    return 'rezolvat'
  })
  const [colet, setColet] = useState<number | null>(null)

  const { data: rez, isError: rezEroare } = useQuery<DeRezolvat>({
    queryKey: ['fan', 'de-rezolvat'],
    queryFn: async () => (await api.get('/fan/de-rezolvat')).data,
    refetchInterval: 60_000,
  })
  const deRezolvat = (rez?.coleteIntarziate ?? 0) + (rez?.dePotrivit ?? 0)

  return (
    <div className="flex flex-col gap-4">
      <Antet />

      <div role="tablist" className="flex gap-1 overflow-x-auto border-b border-b4l-border">
        {(
          [
            ['rezolvat', 'De rezolvat', deRezolvat],
            ['colete', 'Colete', 0],
            ['incasari', 'Încasări', 0],
          ] as const
        ).map(([k, t, n]) => (
          <button
            key={k}
            role="tab"
            aria-selected={fila === k}
            onClick={() => setFila(k)}
            className={`-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2 text-sm ${
              fila === k
                ? 'border-b4l-blue font-semibold text-b4l-text'
                : 'border-transparent text-b4l-text-muted hover:text-b4l-text'
            }`}
          >
            {t}
            {n > 0 && (
              <span className="rounded-full bg-b4l-red px-1.5 text-[11px] font-semibold text-white">
                {n}
              </span>
            )}
          </button>
        ))}
      </div>

      {fila === 'rezolvat' && (
        <FilaDeRezolvat
          rez={rez}
          eroare={rezEroare}
          poateTrimite={poateTrimite}
          onColet={setColet}
        />
      )}
      {fila === 'colete' && (
        <FilaColete qInitial={qInitial} stareInitiala={parametri.get('stare')} onColet={setColet} />
      )}
      {fila === 'incasari' && <FilaIncasari />}

      <PanouColet id={colet} onInchide={() => setColet(null)} />
    </div>
  )
}

/* ── Antet: titlu, starea curierilor, „Actualizeaza" si „Mai multe" ─────────────────────── */

function Antet() {
  const qc = useQueryClient()
  const permisiuni = useAuthStore((s) => s.permissions)
  const poateScrie = permisiuni.includes('fan:write')
  const setariCitire = permisiuni.includes('settings.fan:read')
  const setariScriere = permisiuni.includes('settings.fan:write')
  const [meniu, setMeniu] = useState(false)
  const [fereastra, setFereastra] = useState<null | 'setari' | 'import' | 'istoric' | 'aniVechi'>(
    null,
  )
  const meniuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!meniu) return
    const inafara = (e: MouseEvent) => {
      if (!meniuRef.current?.contains(e.target as Node)) setMeniu(false)
    }
    document.addEventListener('mousedown', inafara)
    return () => document.removeEventListener('mousedown', inafara)
  }, [meniu])

  const { data: st } = useQuery<FanStatus>({
    queryKey: ['fan', 'status'],
    queryFn: async () => (await api.get('/fan/status')).data,
    refetchInterval: 30_000,
  })

  const actualizeaza = useMutation({
    mutationFn: async () => {
      await api.post('/fan/sync', {})
      await api.post('/fan/expeditii/sync', { courier: 'toti' })
    },
    onSuccess: () => {
      toast.success('Se actualizează FAN și Sameday — rezultatul apare în câteva minute.')
      setTimeout(() => qc.invalidateQueries({ queryKey: ['fan'] }), 20_000)
    },
    onError: () => toast.error('Actualizarea n-a pornit. Verifică setările curierilor.'),
  })
  const simplu = (url: string, ok: (d: Record<string, number>) => string) =>
    api
      .post(url, {})
      .then((r) => {
        toast.success(ok(r.data))
        qc.invalidateQueries({ queryKey: ['fan'] })
      })
      .catch(() => toast.error('Acțiunea n-a reușit.'))

  const actiuni: Array<{ t: string; d: string; fa: () => void } | null> = []
  if (poateScrie) {
    actiuni.push(
      {
        t: 'Importă fișier Sameday',
        d: 'Fișierul „ramburs de transferat” primit pe email',
        fa: () => setFereastra('import'),
      },
      {
        t: 'Caută fișiere Sameday în email',
        d: 'Rulează și singur, zilnic la 8:15',
        fa: () => simplu('/fan/sameday-scan-inbox', (d) => `${d.importate ?? 0} fișiere importate`),
      },
      {
        t: 'Adu istoricul FAN',
        d: 'Viramentele dintr-o perioadă trecută',
        fa: () => setFereastra('istoric'),
      },
      {
        t: 'Reia potrivirea',
        d: 'Pentru încasările rămase fără factură',
        fa: () =>
          simplu('/fan/rematch', (d) => `${d.resolved ?? 0} din ${d.checked ?? 0} s-au potrivit`),
      },
      {
        t: 'Închide anii vechi',
        d: 'Încasări nepotrivite dintr-un an încheiat',
        fa: () => setFereastra('aniVechi'),
      },
      null,
    )
  }
  actiuni.push({
    t: 'Descarcă încasările (CSV)',
    d: 'Toate, cu toate câmpurile',
    fa: () =>
      descarcaFisier(
        '/fan/export.csv?status=toate',
        `rambursuri-curieri-${new Date().toISOString().slice(0, 10)}.csv`,
      ).catch(() => toast.error('Exportul a eșuat.')),
  })
  if (setariCitire) {
    actiuni.push(null, {
      t: setariScriere ? 'Setări curieri' : 'Setări curieri (doar citire)',
      d: 'Conturi, conexiuni FAN și Sameday',
      fa: () => setFereastra('setari'),
    })
  }

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold">Rambursuri curieri</h1>
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-b4l-text-muted">
          <StareCurier
            nume="FAN"
            configurat={st?.configurat}
            la={st?.lastSyncAt ?? null}
            eroare={st?.lastError ?? null}
          />
          <StareCurier
            nume="Sameday"
            configurat={st?.sameday?.configurat}
            la={st?.sameday?.lastSyncAt ?? null}
            eroare={st?.sameday?.lastError ?? null}
          />
        </div>
      </div>
      <div className="relative flex gap-2" ref={meniuRef}>
        {poateScrie && (
          <Button onClick={() => actualizeaza.mutate()} loading={actualizeaza.isPending}>
            <RefreshCw size={14} /> Actualizează acum
          </Button>
        )}
        <Button variant="secondary" onClick={() => setMeniu((m) => !m)} aria-expanded={meniu}>
          Mai multe <ChevronDown size={14} />
        </Button>
        {meniu && (
          <div className="absolute right-0 top-full z-30 mt-1.5 w-72 rounded-xl border border-b4l-border bg-white p-1.5 shadow-xl">
            {actiuni.map((a, i) =>
              a ? (
                <button
                  key={a.t}
                  type="button"
                  onClick={() => {
                    setMeniu(false)
                    a.fa()
                  }}
                  className="grid w-full gap-px rounded-md px-2.5 py-2 text-left text-sm hover:bg-b4l-bg-soft"
                >
                  {a.t}
                  <small className="text-xs text-b4l-text-dim">{a.d}</small>
                </button>
              ) : (
                <hr key={`hr-${i}`} className="mx-1 my-1.5 border-b4l-border" />
              ),
            )}
          </div>
        )}
      </div>

      {fereastra === 'setari' && (
        <SetariCurieri editabil={setariScriere} onClose={() => setFereastra(null)} />
      )}
      {fereastra === 'import' && <ImportSameday onClose={() => setFereastra(null)} />}
      {fereastra === 'istoric' && <IstoricFan onGata={() => setFereastra(null)} />}
      {fereastra === 'aniVechi' && <InchideAniVechi onGata={() => setFereastra(null)} />}
    </div>
  )
}

function StareCurier(p: {
  nume: string
  configurat?: boolean
  la: string | null
  eroare: string | null
}) {
  const culoare = p.eroare ? 'bg-b4l-red' : p.configurat ? 'bg-b4l-green' : 'bg-b4l-text-dim'
  return (
    <span className="inline-flex items-center gap-1.5" title={p.eroare ?? undefined}>
      <span className={`h-[7px] w-[7px] rounded-full ${culoare}`} />
      {p.nume}:{' '}
      {p.configurat === false
        ? 'neconfigurat'
        : p.eroare
          ? 'ultima actualizare a eșuat'
          : `actualizat ${cand(p.la)}`}
    </span>
  )
}

/* ── „De rezolvat": carduri-selector si lista aleasa ───────────────────────────────────── */

function FilaDeRezolvat({
  rez,
  eroare,
  poateTrimite,
  onColet,
}: {
  rez: DeRezolvat | undefined
  eroare: boolean
  poateTrimite: boolean
  onColet: (id: number) => void
}) {
  const [lista, setLista] = useState<Lista>('colete')
  return (
    <div className="flex flex-col gap-5">
      <CarduriSelector<Lista>
        eticheta="Ce e de rezolvat"
        ales={lista}
        onAlege={setLista}
        carduri={[
          {
            cheie: 'colete',
            eticheta: 'Colete întârziate',
            valoare: eroare ? '—' : (rez?.coleteIntarziate ?? '…'),
            detaliu: 'la curier de 4+ zile lucrătoare',
            ton: 'rosu',
          },
          {
            cheie: 'potrivit',
            eticheta: 'Încasări de potrivit',
            valoare: eroare ? '—' : (rez?.dePotrivit ?? '…'),
            detaliu: 'nu le găsim factura singuri',
            ton: 'galben',
          },
          {
            cheie: 'trimis',
            eticheta: 'De trimis în EA',
            valoare: eroare ? '—' : (rez?.deTrimis.n ?? '…'),
            detaliu: rez
              ? `${lei(rez.deTrimis.suma)} lei${poateTrimite ? '' : ' · le trimite un admin'}`
              : '',
            ton: 'albastru',
          },
        ]}
      />
      {lista === 'colete' && <ListaColeteIntarziate onColet={onColet} />}
      {lista === 'potrivit' && <ListaDePotrivit />}
      {lista === 'trimis' && <ListaDeTrimis poateTrimite={poateTrimite} />}
    </div>
  )
}

function Sectiune({
  titlu,
  descriere,
  children,
}: {
  titlu: string
  descriere: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-xl border border-b4l-border bg-white">
      <header className="flex flex-wrap items-baseline justify-between gap-2 px-4 pb-2 pt-3">
        <h2 className="text-sm font-semibold">{titlu}</h2>
        <p className="text-xs text-b4l-text-muted">{descriere}</p>
      </header>
      {children}
    </section>
  )
}

/**
 * Eroarea nu e o lista goala (audit U1): „nimic de rezolvat" spus cand serverul n-a raspuns ar
 * ascunde exact problemele pe care ecranul e facut sa le arate.
 */
function GolEroare() {
  return (
    <p className="px-4 pb-5 pt-2 text-sm text-b4l-red-dark">
      Nu am putut aduce lista. Nu înseamnă că e goală — reîncearcă peste câteva momente.
    </p>
  )
}

function Gol({ children }: { children: React.ReactNode }) {
  return <p className="px-4 pb-5 pt-2 text-sm text-b4l-text-muted">{children}</p>
}

function ListaColeteIntarziate({ onColet }: { onColet: (id: number) => void }) {
  const { data, isError: eroare } = useQuery<ExpeditiiResp>({
    queryKey: ['fan', 'expeditii', 'intarziate'],
    queryFn: async () =>
      (await api.get('/fan/expeditii', { params: { stare: 'intarziate', pageSize: 200 } })).data,
  })
  return (
    <Sectiune
      titlu="Colete întârziate"
      descriere="Preluate de curier și nelivrate de 4+ zile lucrătoare. Adminii primesc lista și pe email, la 9:00."
    >
      {eroare ? (
        <GolEroare />
      ) : !data ? (
        <Gol>Se încarcă…</Gol>
      ) : data.rows.length === 0 ? (
        <Gol>
          <b className="text-b4l-green-dark">Niciun colet întârziat.</b> Toate coletele preluate
          s-au livrat la timp sau sunt încă în termen.
        </Gol>
      ) : (
        <TabelColete randuri={data.rows} onDeschide={onColet} coloanaZile="De cât timp" />
      )}
    </Sectiune>
  )
}

function ListaDePotrivit() {
  const [potrivire, setPotrivire] = useState<Ramburs | null>(null)
  const poateScrie = useAuthStore((s) => s.permissions).includes('fan:write')
  const { data, isError: eroare } = useQuery<ListResponse>({
    queryKey: ['fan', 'ramburs', 'de-potrivit'],
    queryFn: async () =>
      (await api.get('/fan/ramburs', { params: { status: 'exceptie', pageSize: 200 } })).data,
  })
  return (
    <Sectiune
      titlu="Încasări de potrivit"
      descriere="Curierul a virat banii, dar nu știm automat pe ce factură."
    >
      {eroare ? (
        <GolEroare />
      ) : !data ? (
        <Gol>Se încarcă…</Gol>
      ) : data.rows.length === 0 ? (
        <Gol>
          <b className="text-b4l-green-dark">Nimic de potrivit.</b> Toate încasările și-au găsit
          factura.
        </Gol>
      ) : (
        <TabelIncasari
          randuri={data.rows}
          coloane="potrivire"
          actiune={
            poateScrie
              ? (r) => (
                  <Button
                    variant="secondary"
                    className="!py-1 text-xs"
                    onClick={() => setPotrivire(r)}
                  >
                    Potrivește…
                  </Button>
                )
              : undefined
          }
        />
      )}
      {potrivire && <PotrivireManuala ramburs={potrivire} onClose={() => setPotrivire(null)} />}
    </Sectiune>
  )
}

function ListaDeTrimis({ poateTrimite }: { poateTrimite: boolean }) {
  const [selectie, setSelectie] = useState<Set<number>>(new Set())
  const { data, isError: eroare } = useQuery<ListResponse>({
    queryKey: ['fan', 'ramburs', 'de-trimis'],
    queryFn: async () =>
      (await api.get('/fan/ramburs', { params: { status: 'potrivit', pageSize: 200 } })).data,
  })
  return (
    <Sectiune
      titlu="De trimis în EA"
      descriere={
        poateTrimite
          ? 'Încasări legate de factură. Bifează și trimite — fiecare factură se verifică în EA înainte.'
          : 'Încasări legate de factură, gata de trimis. Le trimite în EA un admin.'
      }
    >
      <TrimitereAutomata />
      {eroare ? (
        <GolEroare />
      ) : !data ? (
        <Gol>Se încarcă…</Gol>
      ) : data.rows.length === 0 ? (
        <Gol>
          <b className="text-b4l-green-dark">Nimic de trimis.</b> Toate încasările potrivite sunt
          deja în EA.
        </Gol>
      ) : (
        <>
          <TabelIncasari
            randuri={data.rows}
            selectie={poateTrimite ? selectie : undefined}
            onSelectie={setSelectie}
          />
          {data.total > data.rows.length && (
            <p className="border-t border-b4l-border px-4 py-2 text-xs text-b4l-text-dim">
              Primele {data.rows.length} din {data.total}. Restul apar după ce trimiți din acestea.
            </p>
          )}
          {poateTrimite && (
            <BaraTrimitere
              randuri={data.rows}
              selectie={selectie}
              onTrimis={() => setSelectie(new Set())}
            />
          )}
        </>
      )}
    </Sectiune>
  )
}

/* ── Fila „Colete" ─────────────────────────────────────────────────────────────────────── */

const CHIPURI_COLETE = [
  { cheie: 'toate', eticheta: 'Toate', numar: 'toate' },
  { cheie: 'intarziate', eticheta: 'Întârziate', numar: 'intarziate' },
  { cheie: 'nepreluate', eticheta: 'Nepreluate', numar: 'nepreluat' },
  { cheie: 'pe_drum', eticheta: 'Pe drum', numar: null },
  { cheie: 'livrate', eticheta: 'Livrate', numar: 'livrat' },
  { cheie: 'retur', eticheta: 'Retur', numar: 'retur' },
] as const
type ChipColete = (typeof CHIPURI_COLETE)[number]['cheie']

function FilaColete({
  qInitial,
  stareInitiala,
  onColet,
}: {
  qInitial: string
  /** Filtrul cerut de link (`&stare=intarziate` din alerta si email, #800). */
  stareInitiala: string | null
  onColet: (id: number) => void
}) {
  const [stare, setStare] = useState<ChipColete>(() =>
    CHIPURI_COLETE.some((c) => c.cheie === stareInitiala)
      ? (stareInitiala as ChipColete)
      : // Venit spre un AWB anume: se cauta in toate starile, ca sa-l gaseasca oricum.
        qInitial
        ? 'toate'
        : 'intarziate',
  )
  const [q, setQ] = useState(qInitial)
  const [curier, setCurier] = useState<Curier>('toti')
  const [de, setDe] = useState('')
  const [pana, setPana] = useState('')
  const [pagina, setPagina] = useState(0)
  const [marime, setMarime] = useState<PageSize>(50)
  const { data, isError: eroare } = useQuery<ExpeditiiResp>({
    queryKey: ['fan', 'expeditii', stare, q, curier, de, pana, pagina, marime],
    queryFn: async () =>
      (
        await api.get('/fan/expeditii', {
          params: {
            stare,
            courier: curier,
            page: pagina + 1,
            pageSize: marime,
            ...(de ? { from: de } : {}),
            ...(pana ? { to: pana } : {}),
            ...(q.trim() ? { q: q.trim() } : {}),
          },
        })
      ).data,
  })
  const ps = data?.peStare ?? {}
  return (
    <Sectiune
      titlu="Colete"
      descriere="Clic pe un colet pentru drumul complet, comanda și încasarea."
    >
      <div className="flex flex-col gap-3 px-4 pb-3">
        <ChipuriFiltru<ChipColete>
          eticheta="Stare"
          ales={stare}
          onAlege={(k) => {
            setStare(k)
            setPagina(0)
          }}
          chipuri={CHIPURI_COLETE.map((c) => ({
            cheie: c.cheie,
            eticheta: c.eticheta,
            // „Pe drum" = la curier, nefinalizat: in tranzit, in retur sau refuzat.
            numar:
              c.numar == null
                ? (ps.in_tranzit ?? 0) + (ps.in_retur ?? 0) + (ps.refuzat ?? 0)
                : (ps[c.numar] ?? 0),
          }))}
        />
        <Cautare
          valoare={q}
          onSchimba={(v) => (setQ(v), setPagina(0))}
          curier={curier}
          onCurier={setCurier}
          placeholder="Caută AWB, client, comandă, localitate"
        />
        <FiltruData
          eticheta="Data AWB"
          de={de}
          pana={pana}
          onChange={(a, b) => (setDe(a), setPana(b), setPagina(0))}
        />
      </div>
      {eroare ? (
        <GolEroare />
      ) : !data ? (
        <Gol>Se încarcă…</Gol>
      ) : data.rows.length === 0 ? (
        <Gol>Niciun colet pentru filtrul ales.</Gol>
      ) : (
        <>
          <TabelColete randuri={data.rows} onDeschide={onColet} />
          <div className="border-t border-b4l-border px-4 py-3">
            <Pagination
              page={pagina}
              pageSize={marime}
              total={data.total}
              onPageChange={setPagina}
              onPageSizeChange={(m) => (setMarime(m), setPagina(0))}
              itemLabel="colete"
            />
          </div>
        </>
      )}
    </Sectiune>
  )
}

function Cautare(p: {
  valoare: string
  onSchimba: (v: string) => void
  curier: Curier
  onCurier: (c: Curier) => void
  placeholder: string
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <label className="relative min-w-0 flex-[1_1_240px]">
        <Search
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-b4l-text-dim"
        />
        <input
          type="search"
          value={p.valoare}
          onChange={(e) => p.onSchimba(e.target.value)}
          placeholder={p.placeholder}
          aria-label={p.placeholder}
          className="w-full rounded-md border border-b4l-border py-1.5 pl-8 pr-3 text-sm"
        />
      </label>
      <select
        value={p.curier}
        onChange={(e) => p.onCurier(e.target.value as Curier)}
        aria-label="Curier"
        className="rounded-md border border-b4l-border bg-white px-2 py-1.5 text-sm"
      >
        <option value="toti">Toți curierii</option>
        <option value="FAN">FAN</option>
        <option value="SAMEDAY">Sameday</option>
      </select>
    </div>
  )
}

/* ── Fila „Incasari" ───────────────────────────────────────────────────────────────────── */

/** Ziua de acum N zile, YYYY-MM-DD (ora locala) — implicitul filei Incasari: ultimele 30 de zile. */
function acumZile(n: number): string {
  const d = new Date(Date.now() - n * 86_400_000)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function FilaIncasari() {
  const poateTrimite = useAuthStore((s) => s.permissions).includes('fan:post')
  const poateScrie = useAuthStore((s) => s.permissions).includes('fan:write')
  const [stare, setStare] = useState<StareIncasare>('exceptie')
  const [de, setDe] = useState(() => acumZile(30))
  const [pana, setPana] = useState('')
  const [q, setQ] = useState('')
  const [curier, setCurier] = useState<Curier>('toti')
  const [pagina, setPagina] = useState(0)
  const [marime, setMarime] = useState<PageSize>(50)
  const [selectie, setSelectie] = useState<Set<number>>(new Set())
  const [potrivire, setPotrivire] = useState<Ramburs | null>(null)
  const { data, isError: eroare } = useQuery<ListResponse>({
    queryKey: ['fan', 'ramburs', stare, de, pana, q, curier, pagina, marime],
    queryFn: async () =>
      (
        await api.get('/fan/ramburs', {
          params: {
            status: stare,
            courier: curier,
            page: pagina + 1,
            pageSize: marime,
            ...(de ? { from: de } : {}),
            ...(pana ? { to: pana } : {}),
            ...(q.trim() ? { q: q.trim() } : {}),
          },
        })
      ).data,
  })
  const selectabil = stare === 'potrivit' && poateTrimite
  return (
    <Sectiune
      titlu="Încasări"
      descriere="Banii virați de curieri, legați de factura din EA prin numărul comenzii de pe AWB."
    >
      <div className="flex flex-col gap-3 px-4 pb-3">
        <ChipuriFiltru<StareIncasare>
          eticheta="Stare"
          ales={stare}
          onAlege={(k) => {
            setStare(k)
            setPagina(0)
            setSelectie(new Set())
          }}
          chipuri={STARI_INCASARE.map((s) => ({
            cheie: s.cheie,
            eticheta: s.eticheta,
            numar:
              s.cheie === 'toate'
                ? Object.values(data?.peStare ?? {}).reduce((a, n) => a + (n ?? 0), 0)
                : (data?.peStare?.[s.cheie] ?? 0),
          }))}
        />
        <Cautare
          valoare={q}
          onSchimba={(v) => (setQ(v), setPagina(0))}
          curier={curier}
          onCurier={setCurier}
          placeholder="Caută AWB, client, comandă, factură"
        />
        <FiltruData
          eticheta="Data virării"
          de={de}
          pana={pana}
          onChange={(a, b) => (setDe(a), setPana(b), setPagina(0), setSelectie(new Set()))}
        />
      </div>
      {eroare ? (
        <GolEroare />
      ) : !data ? (
        <Gol>Se încarcă…</Gol>
      ) : data.rows.length === 0 ? (
        <Gol>Nimic în această stare pentru perioada aleasă.</Gol>
      ) : (
        <>
          <TabelIncasari
            randuri={data.rows}
            selectie={selectabil ? selectie : undefined}
            onSelectie={setSelectie}
            actiune={
              stare === 'exceptie' && poateScrie
                ? (r) => (
                    <Button
                      variant="secondary"
                      className="!py-1 text-xs"
                      onClick={() => setPotrivire(r)}
                    >
                      Potrivește…
                    </Button>
                  )
                : undefined
            }
          />
          <div className="border-t border-b4l-border px-4 py-3">
            <Pagination
              page={pagina}
              pageSize={marime}
              total={data.total}
              onPageChange={setPagina}
              onPageSizeChange={(m) => (setMarime(m), setPagina(0))}
              itemLabel="încasări"
            />
          </div>
          {selectabil && (
            <BaraTrimitere
              randuri={data.rows}
              selectie={selectie}
              onTrimis={() => setSelectie(new Set())}
            />
          )}
        </>
      )}
      <ViramentePeZile courier="FAN" />
      <CompensareFan />
      <ViramentePeZile courier="SAMEDAY" />
      {potrivire && <PotrivireManuala ramburs={potrivire} onClose={() => setPotrivire(null)} />}
    </Sectiune>
  )
}

/* ── Ferestrele din „Mai multe" ────────────────────────────────────────────────────────── */

/** Istoricul FAN pe o perioada — cu calendar, in locul celor doua ferestre AAAA-LL-ZZ. */
function IstoricFan({ onGata }: { onGata: () => void }) {
  const [de, setDe] = useState('')
  const [pana, setPana] = useState('')
  const m = useMutation({
    mutationFn: async () => (await api.post('/fan/sync-istoric', { from: de, to: pana })).data,
    onSuccess: () => {
      toast.success('Istoricul se aduce în fundal — o zi pe rând, durează câteva minute.')
      onGata()
    },
    onError: () => toast.error('Istoricul n-a pornit.'),
  })
  return (
    <Modal
      open
      onClose={onGata}
      title="Adu istoricul FAN"
      inchideLaClicInAfara={false}
      footer={
        <>
          <Button variant="secondary" onClick={onGata}>
            Renunță
          </Button>
          <Button
            onClick={() => m.mutate()}
            loading={m.isPending}
            disabled={!de || !pana || de > pana}
          >
            Adu istoricul
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-b4l-text-muted">
        Viramentele FAN din perioada aleasă, câte o zi. Ce e deja trimis în EA nu se atinge.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <DateField label="De la" value={de} onChange={setDe} />
        <DateField label="Până la" value={pana} onChange={setPana} />
      </div>
    </Modal>
  )
}

/** Inchiderea in masa a unui an incheiat — cu an ales, in locul ferestrelor native. */
function InchideAniVechi({ onGata }: { onGata: () => void }) {
  const qc = useQueryClient()
  const an = new Date().getFullYear()
  const [pana, setPana] = useState(String(an))
  const m = useMutation({
    mutationFn: async () =>
      (await api.post('/fan/inchide-vechi', { panaLa: `${pana}-01-01`, redeschide: false })).data,
    onSuccess: (r: { randuri?: number }) => {
      toast.success(`${r.randuri ?? 0} încasări închise`)
      qc.invalidateQueries({ queryKey: ['fan'] })
      onGata()
    },
    onError: () => toast.error('Închiderea n-a reușit.'),
  })
  return (
    <Modal
      open
      onClose={onGata}
      title="Închide anii vechi"
      inchideLaClicInAfara={false}
      footer={
        <>
          <Button variant="secondary" onClick={onGata}>
            Renunță
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending}>
            Închide înainte de 1 ianuarie {pana}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-b4l-text-muted">
        Încasările <b>nepotrivite</b> virate înainte de 1 ianuarie a anului ales ies din lista de
        lucru. Nu se șterge nimic: rămân căutabile, la „Închise”, și se pot readuce.
      </p>
      <label htmlFor="an-vechi" className="mb-1 block text-xs font-medium">
        Închide tot ce e înainte de
      </label>
      <select
        id="an-vechi"
        value={pana}
        onChange={(e) => setPana(e.target.value)}
        className="rounded-md border border-b4l-border bg-white px-2 py-1.5 text-sm"
      >
        {[an, an - 1, an - 2].map((a) => (
          <option key={a} value={a}>
            1 ianuarie {a}
          </option>
        ))}
      </select>
    </Modal>
  )
}
